import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  return (
    <>
      <SEO
        title="Privacy Policy - Couplendar"
        description="Read Couplendar's privacy policy to understand how we collect, use, and protect your personal information and relationship data."
        url="https://couplendar.com/privacy-policy"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to Couplendar ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application and services.
            </Typography>
            <Typography variant="body1" paragraph>
              Couplendar is an application designed to help couples track shared activities, chores, and time-off to promote equality and balance in relationships.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              2. Information We Collect
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.1 Personal Information
            </Typography>
            <Typography variant="body1" component="div">
              We collect the following personal information:
              <ul>
                <li><strong>Account Information:</strong> Email address, username, and password (encrypted)</li>
                <li><strong>Profile Information:</strong> Display name and profile preferences</li>
                <li><strong>Relationship Data:</strong> Partner connections and relationship settings</li>
              </ul>
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.2 Usage Data
            </Typography>
            <Typography variant="body1" component="div">
              We collect information about your use of the application:
              <ul>
                <li><strong>Activity Tracking:</strong> Chores completed, time-off entries, and daily activities you record</li>
                <li><strong>Statistics:</strong> Aggregated data about your relationship activities and patterns</li>
                <li><strong>Feedback:</strong> Any feedback or comments you provide through our feedback system</li>
              </ul>
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.3 Technical Information
            </Typography>
            <Typography variant="body1" component="div">
              We automatically collect:
              <ul>
                <li><strong>Log Data:</strong> IP address, browser type, access times, and pages viewed</li>
                <li><strong>Device Information:</strong> Device type, operating system, and unique device identifiers</li>
                <li><strong>Authentication Tokens:</strong> JWT tokens for session management (stored securely)</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" component="div">
              We use your information for the following purposes:
              <ul>
                <li><strong>Service Provision:</strong> To provide, maintain, and improve our services</li>
                <li><strong>Authentication:</strong> To verify your identity and manage your account</li>
                <li><strong>Communication:</strong> To send you service-related emails (verification, password reset, important updates)</li>
                <li><strong>Analytics:</strong> To understand how users interact with our application and improve user experience</li>
                <li><strong>Security:</strong> To detect, prevent, and address technical issues and security threats</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              4. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell your personal information. We may share your information only in the following circumstances:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li><strong>With Your Partner:</strong> Relationship data is shared with your connected partner within the application</li>
                <li><strong>Service Providers:</strong> We use third-party services that may process your data:
                  <ul>
                    <li>Aiven Cloud (database hosting with SSL/TLS encryption)</li>
                    <li>Email service provider (Gmail SMTP for transactional emails)</li>
                  </ul>
                </li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to users)</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              5. Data Security
            </Typography>
            <Typography variant="body1" component="div">
              We implement industry-standard security measures to protect your information:
              <ul>
                <li><strong>Encryption:</strong> Passwords are hashed using secure algorithms; data transmission uses HTTPS/TLS</li>
                <li><strong>Database Security:</strong> PostgreSQL database hosted on Aiven Cloud with SSL/TLS encryption</li>
                <li><strong>Authentication:</strong> JWT-based authentication with token rotation and blacklisting</li>
                <li><strong>Access Controls:</strong> Limited access to personal data on a need-to-know basis</li>
                <li><strong>Monitoring:</strong> Security monitoring and logging to detect unauthorized access</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              6. Your Privacy Rights
            </Typography>
            <Typography variant="body1" paragraph>
              Depending on your location, you may have the following rights regarding your personal data:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data (via account deletion in settings)</li>
                <li><strong>Data Portability:</strong> Request transfer of your data in a structured, machine-readable format</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where consent is the legal basis</li>
                <li><strong>Object:</strong> Object to processing of your personal data in certain circumstances</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph>
              To exercise these rights, please contact us at: <strong>support@couplendar.com</strong>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              7. Data Retention
            </Typography>
            <Typography variant="body1" component="div">
              We retain your information for as long as necessary to provide our services and comply with legal obligations:
              <ul>
                <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
                <li><strong>Deleted Accounts:</strong> Upon account deletion, personal data is permanently removed within 30 days</li>
                <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law</li>
                <li><strong>Backups:</strong> Data in backups is deleted according to our backup retention schedule (up to 90 days)</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              8. Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to provide and improve our services. You can manage your cookie preferences through our cookie consent banner.
            </Typography>
            <Typography variant="body1" component="div">
              Cookie categories:
              <ul>
                <li><strong>Necessary Cookies:</strong> Essential for authentication and core functionality (cannot be disabled)</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Help us understand usage patterns and improve the application</li>
                <li><strong>Marketing Cookies:</strong> Used for advertising purposes (if applicable)</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              9. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Couplendar is not intended for users under the age of 16. We do not knowingly collect personal information from children under 16. If you believe we have collected information from a child under 16, please contact us immediately.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              10. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable laws.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              11. Third-Party Links
            </Typography>
            <Typography variant="body1" paragraph>
              Our application may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              12. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Updating the "Last Updated" date at the top of this policy</li>
                <li>Sending you an email notification (for significant changes)</li>
                <li>Displaying a prominent notice within the application</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph>
              Your continued use of our services after changes become effective constitutes acceptance of the updated Privacy Policy.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              13. Contact Us
            </Typography>
            <Typography variant="body1" component="div">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              <Box sx={{ mt: 2, ml: 2 }}>
                <Typography variant="body1">
                  <strong>Email:</strong> support@couplendar.com
                </Typography>
              </Box>
            </Typography>
          </section>

          <Divider sx={{ my: 3 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              GDPR Notice for European Users
            </Typography>
            <Typography variant="body1" paragraph>
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have additional rights under the General Data Protection Regulation (GDPR):
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li><strong>Legal Basis:</strong> We process your data based on consent, contract performance, and legitimate interests</li>
                <li><strong>Data Controller:</strong> Couplendar is the data controller for your personal information</li>
                <li><strong>Right to Lodge a Complaint:</strong> You have the right to lodge a complaint with your local data protection authority</li>
                <li><strong>Data Protection Contact:</strong> Contact support@couplendar.com for GDPR-related inquiries</li>
              </ul>
            </Typography>
          </section>
        </Box>
      </Paper>
    </Container>
    </>
  );
};

export default PrivacyPolicyPage;
